import React, { HTMLProps } from 'react';
import { colors, M } from '@dashboard-experience/mastodon';
import T from 'components/T';
import styled from 'styled-components';
import SubmitButton from '../../fields/SubmitButton';
import { useNavSlotState } from './NavSlotContext';

const ButtonStyled = styled(M.Button)`
  .mastodon-icon {
    margin-right: 5px;
  }
`;

const VerificationNavigationStyled = styled.div`
  background: ${colors.white};
  border-top: 1px solid #ddd;
  position: fixed;
  bottom: 0;
  left: 0;
  padding: 0.75rem;
  margin: 0;
  width: 100%;
  z-index: 1001;

  @media (min-width: 768px) {
    background: none;
    border: 0;
    padding: 0;
    position: static;
    width: auto;
    left: auto;
    bottom: auto;
  }

  display: flex;
`;

const SubmitButtonWrapperStyled = styled.span`
  margin-left: auto;
`;

interface PreviousButtonProps extends HTMLProps<HTMLButtonElement> {}

const PreviousButton: React.FC<PreviousButtonProps> = ({
  onClick,
  disabled,
}) => (
  <ButtonStyled
    kind='secondary'
    type='button'
    onClick={onClick}
    disabled={disabled}
  >
    <M.Icon icon='ChevronLeft' /> <T value='buttons.previous' />
  </ButtonStyled>
);

interface VerificationNavigationProps {
  children?: any;
  isFinalStep: boolean;
  isValid: boolean;
  previousStep: React.MouseEventHandler<HTMLButtonElement>;
  showNextStepBtn?: boolean;
  showPreviousBtn?: boolean;
  submitting?: boolean;
  hideNavigation?: boolean;
}

const VerificationNavigation: React.FC<VerificationNavigationProps> = ({
  children = null,
  isFinalStep = false,
  isValid = false,
  previousStep = () => {},
  showNextStepBtn = false,
  showPreviousBtn = false,
  submitting = false,
  hideNavigation = false,
}) => {
  // EVERI-3325: a panel can hide the page-level Submit button via the NavSlot
  // context (multi-doc on-flag has no Save/Submit button — the modal confirms
  // finalize submission). Defaults to false for every other flow.
  const { suppressSubmit, disablePrevious } = useNavSlotState();
  if (hideNavigation) {
    return <>children</>;
  }
  return (
    <VerificationNavigationStyled role='navigation'>
      {showPreviousBtn && (
        <div>
          <PreviousButton
            onClick={disablePrevious ? undefined : previousStep}
            disabled={disablePrevious}
          />
        </div>
      )}
      {children}
      {showNextStepBtn && !suppressSubmit && (
        <SubmitButtonWrapperStyled>
          &nbsp;
          <SubmitButton
            submitting={submitting}
            isFinalStep={isFinalStep}
            disabled={!isValid}
          />
        </SubmitButtonWrapperStyled>
      )}
    </VerificationNavigationStyled>
  );
};
export const GenericNavigation: React.FC<{
  previousBtn: React.ReactNode;
  nextBtn: React.ReactNode;
  children: React.ReactNode;
}> = ({ previousBtn, nextBtn, children }) => (
  <VerificationNavigationStyled role='navigation'>
    <div>{previousBtn}</div>
    {children}
    <SubmitButtonWrapperStyled>{nextBtn}</SubmitButtonWrapperStyled>
  </VerificationNavigationStyled>
);

export default VerificationNavigation;
